import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { cvAPI, refreshTier } from '../services/api';
import api from '../services/api';
import Sidebar from '../components/Sidebar';
import PlanBadge from '../components/PlanBadge';
import WelcomeModal from '../components/WelcomeModal';
import WelcomeCard from '../components/WelcomeCard';
import AppTour from '../components/AppTour';
import { SEEKER_TOUR_STEPS } from '../data/tourSteps';
import '../styles/theme.css';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [cvInfo, setCvInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showCVModal, setShowCVModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [stats, setStats] = useState({ applications: 0, matches: 0, interviews: 0, views: 0 });
  const [userName, setUserName] = useState('');
  const [userTier, setUserTier] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    refreshTier();
    fetchCVInfo();
    fetchStats();
    const name = localStorage.getItem('user_name') || 'there';
    setUserName(name.split(' ')[0]);
    const tier = localStorage.getItem('user_tier');
    setUserTier(tier);

    // Welcome modal: show once after upgrade
    const showModal = localStorage.getItem('show_welcome_modal') === 'true';
    if (showModal && (tier === 'pro' || tier === 'premium')) {
      setShowWelcomeModal(true);
    }

    // Welcome card: show until dismissed
    const userId = localStorage.getItem('user_id') || name;
    const cardDismissed = localStorage.getItem(`welcome_card_dismissed_${userId}`) === 'true';
    if (!cardDismissed && (tier === 'pro' || tier === 'premium')) {
      setShowWelcomeCard(true);
    }

    // App tour: show once for new users
    const tourDone = localStorage.getItem('seeker_tour_done') === 'true';
    if (!tourDone) setShowTour(true);
  }, []);

  const fetchCVInfo = async () => {
    try {
      // Check documents API first (where users upload via Documents page)
      const docsRes = await api.get('/documents/');
      const cvDocs = docsRes.data.documents?.filter(d => d.document_type === 'cv') || [];
      if (cvDocs.length > 0) {
        const primary = cvDocs.find(d => d.is_primary) || cvDocs[0];
        // Also fetch extracted text from CV table (populated on upload)
        let extractedText = null;
        try {
          const cvRes = await cvAPI.getMyCV();
          extractedText = cvRes.data.cv?.extracted_text || null;
        } catch {}
        setCvInfo({
          filename: primary.file_name,
          file_size: primary.file_size,
          created_at: primary.uploaded_at,
          extracted_text: extractedText,
        });
        return;
      }
      // Fallback: legacy /cv/my-cv endpoint
      const response = await cvAPI.getMyCV();
      if (response.data.cv) setCvInfo(response.data.cv);
    } catch (error) {}
  };

  const fetchStats = async () => {
    try {
      const [appRes, matchRes] = await Promise.allSettled([
        api.get('/applications/my-applications/'),
        api.get('/matching/matches')
      ]);
      const apps = appRes.status === 'fulfilled' ? appRes.value.data : [];
      const matches = matchRes.status === 'fulfilled' ? matchRes.value.data : [];
      setStats({
        applications: Array.isArray(apps) ? apps.length : 0,
        matches: Array.isArray(matches) ? matches.length : 0,
        interviews: Array.isArray(apps) ? apps.filter(a => a.status === 'interview').length : 0,
        views: Array.isArray(apps) ? apps.filter(a => a.status === 'viewed').length : 0,
      });
    } catch (error) {}
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) { setUploadError('Please upload a PDF or DOCX file'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('File size must be less than 5MB'); return; }
    setUploading(true); setUploadError('');
    try {
      // Upload via documents endpoint so Dashboard and Documents page stay in sync
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'cv');
      fd.append('is_primary', 'true');
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchCVInfo();
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Failed to upload CV');
    } finally { setUploading(false); }
  };

  const handleDeleteCV = async () => {
    if (!window.confirm('Delete your CV?')) return;
    try {
      // Fetch CV docs and delete them all — backend now cleans up CV table too
      const docsRes = await api.get('/documents/');
      const cvDocs = docsRes.data.documents?.filter(d => d.document_type === 'cv') || [];
      await Promise.all(cvDocs.map(d => api.delete(`/documents/${d.id}`)));
      // Also clean up CV table in case it was uploaded via the old /cv/upload endpoint
      await cvAPI.deleteMyCV().catch(() => {});
      setCvInfo(null);
    } catch (error) {}
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const quickActions = [
    { icon: 'fa-bullseye', label: 'Find Matches', desc: 'AI-powered job matching', color: 'blue', path: '/matched-jobs', disabled: !cvInfo },
    { icon: 'fa-search', label: 'Browse Jobs', desc: 'Explore all opportunities', color: 'purple', path: '/jobs', disabled: false },
    { icon: 'fa-folder-open', label: 'Documents', desc: 'Manage your files', color: 'green', path: '/documents', disabled: false },
    { icon: 'fa-bolt', label: 'Auto-Apply', desc: 'Automate applications', color: 'orange', path: '/auto-apply-settings', disabled: !cvInfo },
    { icon: 'fa-paper-plane', label: 'Applications', desc: 'Track your progress', color: 'blue', path: '/my-applications', disabled: false },
    { icon: 'fa-user', label: 'My Profile', desc: 'Update your info', color: 'purple', path: '/profile', disabled: false },
  ];

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        {/* Topbar */}
        <div className="portal-topbar">
          <span className="topbar-title">Dashboard</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme">
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
            <button className="topbar-btn" onClick={() => navigate('/profile')} title="Profile">
              <i className="fas fa-user"></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          {/* Welcome */}
          <div className="page-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1>Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {userName} 👋</h1>
              <PlanBadge tier={userTier} />
            </div>
            <p>Here's what's happening with your job search today.</p>
          </div>

          {/* Welcome Card — shown until dismissed */}
          {showWelcomeCard && (
            <WelcomeCard
              tier={userTier}
              userName={userName}
              onDismiss={() => {
                const userId = localStorage.getItem('user_id') || localStorage.getItem('user_name') || 'user';
                localStorage.setItem(`welcome_card_dismissed_${userId}`, 'true');
                setShowWelcomeCard(false);
              }}
            />
          )}

          {/* Stats */}
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-icon-wrap blue"><i className="fas fa-paper-plane"></i></div>
              <div className="stat-info">
                <div className="stat-value">{stats.applications}</div>
                <div className="stat-label">Applications Sent</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap green"><i className="fas fa-bullseye"></i></div>
              <div className="stat-info">
                <div className="stat-value">{stats.matches}</div>
                <div className="stat-label">Job Matches</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap orange"><i className="fas fa-calendar-check"></i></div>
              <div className="stat-info">
                <div className="stat-value">{stats.interviews}</div>
                <div className="stat-label">Interviews</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap purple"><i className="fas fa-eye"></i></div>
              <div className="stat-info">
                <div className="stat-value">{stats.views}</div>
                <div className="stat-label">Profile Views</div>
              </div>
            </div>
          </div>

          <div className="dashboard-grid">
            {/* CV Card */}
            <div className="card cv-card" data-tour="cv-upload">
              <div className="card-header">
                <span className="card-title"><i className="fas fa-file-alt" style={{color:'var(--primary)', marginRight:'0.5rem'}}></i>Your CV</span>
                {cvInfo && (
                  <span className="badge badge-green"><i className="fas fa-check"></i> Uploaded</span>
                )}
              </div>

              {cvInfo ? (
                <div className="cv-uploaded">
                  <div className="cv-file-row">
                    <div className="cv-file-icon"><i className="fas fa-file-pdf"></i></div>
                    <div className="cv-file-info">
                      <div className="cv-file-name">{cvInfo.filename || 'CV Document'}</div>
                      <div className="cv-file-meta">
                        {cvInfo.file_size ? formatFileSize(cvInfo.file_size) + ' · ' : ''}
                        Uploaded {new Date(cvInfo.created_at || cvInfo.uploaded_at).toLocaleDateString('en-ZA')}
                      </div>
                    </div>
                  </div>
                  {cvInfo.extracted_text && (
                    <div className="cv-preview-box">
                      <p>{truncateText(cvInfo.extracted_text, 120)}</p>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowCVModal(true)}>
                        View full text <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  )}
                  <div className="cv-actions-row">
                    <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current.click()} disabled={uploading}>
                      <i className="fas fa-sync-alt"></i> {uploading ? 'Uploading...' : 'Replace'}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={handleDeleteCV}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="cv-empty">
                  <div className="cv-empty-icon"><i className="fas fa-cloud-upload-alt"></i></div>
                  <p>Upload your CV to start getting matched with jobs</p>
                  {uploadError && <div className="alert alert-error" style={{marginTop:'0.75rem'}}>{uploadError}</div>}
                  <button className="btn btn-primary btn-full" onClick={() => fileInputRef.current.click()} disabled={uploading} style={{marginTop:'1rem'}}>
                    <i className="fas fa-upload"></i> {uploading ? 'Uploading...' : 'Upload CV'}
                  </button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileSelect} style={{ display: 'none' }} />
            </div>

            {/* Quick Actions */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Quick Actions</span>
              </div>
              <div className="quick-actions-grid">
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    className={`quick-action-btn ${action.disabled ? 'disabled' : ''}`}
                    onClick={() => !action.disabled && navigate(action.path)}
                    disabled={action.disabled}
                    title={action.disabled ? 'Upload CV first' : action.desc}
                  >
                    <div className={`qa-icon qa-${action.color}`}>
                      <i className={`fas ${action.icon}`}></i>
                    </div>
                    <div className="qa-info">
                      <div className="qa-label">{action.label}</div>
                      <div className="qa-desc">{action.disabled ? 'Upload CV first' : action.desc}</div>
                    </div>
                    <i className="fas fa-chevron-right qa-arrow"></i>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Welcome Modal — shown once after upgrade */}
      {showWelcomeModal && (
        <WelcomeModal
          tier={userTier}
          userName={userName}
          onClose={() => {
            localStorage.removeItem('show_welcome_modal');
            setShowWelcomeModal(false);
          }}
        />
      )}

      {/* App Tour — shown once for new users */}
      {showTour && (
        <AppTour
          steps={SEEKER_TOUR_STEPS}
          storageKey="seeker_tour_done"
          onEnd={() => setShowTour(false)}
        />
      )}

      {/* CV Modal */}
      {showCVModal && cvInfo?.extracted_text && (
        <div className="modal-overlay" onClick={() => setShowCVModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-file-alt" style={{color:'var(--primary)', marginRight:'0.5rem'}}></i>CV Extracted Text</h2>
              <button className="modal-close" onClick={() => setShowCVModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <pre style={{fontFamily:'monospace', fontSize:'0.85rem', whiteSpace:'pre-wrap', color:'var(--text-secondary)', lineHeight:'1.7', background:'var(--surface-2)', padding:'1rem', borderRadius:'var(--radius-md)'}}>{cvInfo.extracted_text}</pre>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCVModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
